"use client";

import { useEffect, useState, useMemo, useRef } from "react";
import Image from "next/image";
import { useLocale, useTranslations } from "next-intl";
import { ConcursosComerciales } from "@/lib/api/datosGenerales/types/concursos";
import { fetchProcesosComerciales } from "@/lib/api/datosGenerales/fetchConcursos";
import {
  Calendar,
  Clock,
  Download,
  Tag,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  X,
  Check,
  Search,
  Loader2,
} from "lucide-react";
import { type Locale, intlTag } from "@/i18n/config";

const HERO_IMAGE =
  "https://galeria.tocumenaero.com/gallery/api/locales%20generales/321A5026.jpg";

const ITEMS_PER_PAGE = 10;

function getPageList(current: number, total: number): (number | "...")[] {
  if (total <= 7) {
    return Array.from({ length: total }, (_, i) => i + 1);
  }
  const pages: (number | "...")[] = [1];
  if (current > 3) pages.push("...");
  const start = Math.max(2, current - 1);
  const end = Math.min(total - 1, current + 1);
  for (let i = start; i <= end; i++) pages.push(i);
  if (current < total - 2) pages.push("...");
  pages.push(total);
  return pages;
}

function formatDate(dateString: string, locale: Locale): string {
  const date = new Date(dateString);
  return date.toLocaleDateString(intlTag(locale), {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}

function FilterDropdown({
  label,
  value,
  options,
  onChange,
  allLabel,
}: {
  label: string;
  value: string | null;
  options: string[];
  onChange: (value: string | null) => void;
  allLabel: string;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handleClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", handleClick);
    document.addEventListener("keydown", handleKey);
    return () => {
      document.removeEventListener("mousedown", handleClick);
      document.removeEventListener("keydown", handleKey);
    };
  }, [open]);

  const displayValue = value ?? allLabel;

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen((o) => !o)}
        className={`inline-flex items-center justify-between gap-3 min-w-[220px] px-4 py-2.5 rounded-full text-xs font-semibold uppercase tracking-wider transition-colors border ${
          value
            ? "bg-[#102542] text-white border-[#102542]"
            : "bg-white text-gray-700 border-gray-200 hover:border-[#102542]"
        }`}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span className="flex items-center gap-2 truncate">
          <span
            className={`opacity-70 ${value ? "text-white/80" : "text-gray-400"}`}
          >
            {label}:
          </span>
          <span className="truncate normal-case tracking-normal font-medium">
            {displayValue}
          </span>
        </span>
        <ChevronDown
          className={`w-3.5 h-3.5 flex-shrink-0 transition-transform ${open ? "rotate-180" : ""}`}
        />
      </button>

      {open && (
        <div
          role="listbox"
          className="absolute top-full left-0 mt-2 z-40 min-w-full max-w-xs max-h-72 overflow-y-auto bg-white border border-gray-200 rounded-2xl shadow-lg py-2"
        >
          <button
            onClick={() => {
              onChange(null);
              setOpen(false);
            }}
            className={`w-full text-left px-4 py-2 text-sm flex items-center justify-between transition-colors ${
              value === null
                ? "text-[#102542] font-semibold bg-gray-50"
                : "text-gray-700 hover:bg-gray-50"
            }`}
          >
            <span>{allLabel}</span>
            {value === null && <Check className="w-4 h-4" />}
          </button>
          <div className="my-1 border-t border-gray-100" />
          {options.map((opt) => (
            <button
              key={opt}
              onClick={() => {
                onChange(opt);
                setOpen(false);
              }}
              className={`w-full text-left px-4 py-2 text-sm flex items-center justify-between transition-colors ${
                value === opt
                  ? "text-[#102542] font-semibold bg-gray-50"
                  : "text-gray-700 hover:bg-gray-50"
              }`}
            >
              <span className="truncate">{opt}</span>
              {value === opt && <Check className="w-4 h-4 flex-shrink-0" />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

type EstadoVariant = "open" | "closed" | "upcoming" | "default";

function getEstadoVariant(estado: string): EstadoVariant {
  const e = estado?.toLowerCase() ?? "";
  if (e.includes("abierto") || e.includes("open")) return "open";
  if (e.includes("cerrado") || e.includes("closed")) return "closed";
  if (e.includes("próximo") || e.includes("proximo") || e.includes("upcoming"))
    return "upcoming";
  return "default";
}

const ESTADO_STYLES: Record<EstadoVariant, string> = {
  open: "bg-emerald-50 text-emerald-700 border-emerald-200",
  closed: "bg-red-50 text-red-700 border-red-200",
  upcoming: "bg-amber-50 text-amber-700 border-amber-200",
  default: "bg-gray-50 text-gray-700 border-gray-200",
};

export default function ConcursosPage() {
  const t = useTranslations("concursosComercialesPage");
  const locale = useLocale() as Locale;
  const [concursos, setConcursos] = useState<ConcursosComerciales[]>([]);
  const [loading, setLoading] = useState(true);
  const [estadoSeleccionado, setEstadoSeleccionado] = useState<string | null>(
    null,
  );
  const [rubroSeleccionado, setRubroSeleccionado] = useState<string | null>(
    null,
  );
  const [busqueda, setBusqueda] = useState("");
  const [isSearching, setIsSearching] = useState(false);

  useEffect(() => {
    if (!busqueda) {
      setIsSearching(false);
      return;
    }
    setIsSearching(true);
    const timer = setTimeout(() => setIsSearching(false), 300);
    return () => clearTimeout(timer);
  }, [busqueda]);

  useEffect(() => {
    async function loadData() {
      try {
        const data = await fetchProcesosComerciales();
        setConcursos(Array.isArray(data) ? data : []);
      } catch (error) {
        console.error("Error:", error);
      } finally {
        setLoading(false);
      }
    }
    loadData();
  }, [locale]);

  const estadosDisponibles = useMemo(
    () => Array.from(new Set(concursos.map((c) => c.estado).filter(Boolean))),
    [concursos],
  );

  const rubrosDisponibles = useMemo(
    () =>
      Array.from(
        new Set(concursos.flatMap((c) => c.rubros.map((r) => r.nombre))),
      ).sort(),
    [concursos],
  );

  const concursosFiltrados = useMemo(() => {
    const searchLower = busqueda.trim().toLowerCase();
    return concursos.filter((c) => {
      const coincideEstado = estadoSeleccionado
        ? c.estado === estadoSeleccionado
        : true;
      const coincideRubro = rubroSeleccionado
        ? c.rubros.some((r) => r.nombre === rubroSeleccionado)
        : true;
      const coincideBusqueda = searchLower
        ? c.titulo?.toLowerCase().includes(searchLower)
        : true;
      return coincideEstado && coincideRubro && coincideBusqueda;
    });
  }, [concursos, estadoSeleccionado, rubroSeleccionado, busqueda]);

  const [page, setPage] = useState(1);

  const totalPages = Math.max(
    1,
    Math.ceil(concursosFiltrados.length / ITEMS_PER_PAGE),
  );

  const paginatedConcursos = useMemo(
    () =>
      concursosFiltrados.slice(
        (page - 1) * ITEMS_PER_PAGE,
        page * ITEMS_PER_PAGE,
      ),
    [concursosFiltrados, page],
  );

  useEffect(() => {
    setPage(1);
  }, [estadoSeleccionado, rubroSeleccionado, busqueda]);

  const limpiarFiltros = () => {
    setEstadoSeleccionado(null);
    setRubroSeleccionado(null);
    setBusqueda("");
    setPage(1);
  };

  const hayFiltrosActivos =
    estadoSeleccionado !== null ||
    rubroSeleccionado !== null ||
    busqueda !== "";

  const resultsLabel =
    concursosFiltrados.length === 1
      ? t("resultsOne", { count: concursosFiltrados.length })
      : t("resultsMany", { count: concursosFiltrados.length });

  function getTimeRemaining(
    endDate: string,
  ): { text: string; urgent: boolean } | null {
    const now = new Date();
    const end = new Date(endDate);
    const diff = end.getTime() - now.getTime();
    if (diff <= 0) return null;

    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));

    if (days > 30) {
      const months = Math.floor(days / 30);
      return { text: t("remainingMonths", { count: months }), urgent: false };
    }
    if (days > 0) {
      return { text: t("remainingDays", { count: days }), urgent: days <= 7 };
    }
    return { text: t("remainingHours", { count: hours }), urgent: true };
  }

  return (
    <main className="min-h-screen bg-white/50">
      {/* Hero */}
      <section className="relative h-[55vh] min-h-[420px] flex items-end overflow-hidden">
        <Image
          src={HERO_IMAGE}
          alt={t("title")}
          fill
          className="object-cover"
          priority
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-black/10" />
        <div className="relative z-10 max-w-[80%] mx-auto w-full pb-16 md:pb-20">
          <div className="max-w-3xl">
            <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-white font-semibold mb-4">
              <span className="w-8 h-px bg-white" />
              {t("eyebrow")}
            </span>
            <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-white mb-4 tracking-tight text-balance">
              {t("title")}
            </h1>
            <p className="text-white/80 text-base md:text-lg leading-relaxed">
              {t("subtitle")}
            </p>
          </div>
        </div>
      </section>

      {/* Filtros */}
      {!loading && concursos.length > 0 && (
        <section className="border-b border-gray-200 sticky top-0 z-30 bg-white/95 backdrop-blur">
          <div className="max-w-[80%] mx-auto py-5 space-y-4">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div className="relative w-full md:max-w-md">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  placeholder={t("searchPlaceholder")}
                  value={busqueda}
                  onChange={(e) => setBusqueda(e.target.value)}
                  className="w-full pl-11 pr-10 py-2.5 bg-white border border-gray-200 rounded-full text-sm
                     focus:outline-none focus:border-[#102542] transition-colors text-gray-900 placeholder-gray-400"
                />
                {isSearching && busqueda && (
                  <Loader2 className="absolute right-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[#102542] animate-spin" />
                )}
                {busqueda && !isSearching && (
                  <button
                    onClick={() => setBusqueda("")}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-700 transition-colors"
                    aria-label={t("clear")}
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>

              <div className="flex flex-wrap items-center gap-3">
                {estadosDisponibles.length > 0 && (
                  <FilterDropdown
                    label={t("filtersStatus")}
                    value={estadoSeleccionado}
                    options={estadosDisponibles}
                    onChange={setEstadoSeleccionado}
                    allLabel={t("filterAll")}
                  />
                )}
                {rubrosDisponibles.length > 0 && (
                  <FilterDropdown
                    label={t("filtersCategory")}
                    value={rubroSeleccionado}
                    options={rubrosDisponibles}
                    onChange={setRubroSeleccionado}
                    allLabel={t("filterAll")}
                  />
                )}
              </div>
            </div>

            <div className="flex items-center justify-between text-xs uppercase tracking-wider">
              <span className="text-gray-500 font-mono">{resultsLabel}</span>
              {hayFiltrosActivos && (
                <button
                  onClick={limpiarFiltros}
                  className="inline-flex items-center gap-1.5 text-[#102542] font-semibold hover:gap-2 transition-all"
                >
                  <X className="w-3.5 h-3.5" />
                  {t("clear")}
                </button>
              )}
            </div>
          </div>
        </section>
      )}

      {/* Loading */}
      {loading && (
        <section className="max-w-[80%] mx-auto py-16">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-px bg-gray-200 rounded-2xl overflow-hidden border border-gray-200">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="bg-white p-8 space-y-4">
                <div className="h-4 w-24 bg-gray-100 rounded animate-pulse" />
                <div className="h-6 w-3/4 bg-gray-100 rounded animate-pulse" />
                <div className="h-4 w-1/2 bg-gray-100 rounded animate-pulse" />
                <div className="h-4 w-2/3 bg-gray-100 rounded animate-pulse" />
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Empty global */}
      {!loading && concursos.length === 0 && (
        <section className="max-w-[80%] mx-auto py-24 text-center">
          <h3 className="text-2xl font-bold text-gray-900 mb-3 tracking-tight">
            {t("emptyTitle")}
          </h3>
          <p className="text-gray-600">{t("emptyDescription")}</p>
        </section>
      )}

      {/* Empty filtros */}
      {!loading &&
        concursos.length > 0 &&
        concursosFiltrados.length === 0 && (
          <section className="max-w-[80%] mx-auto py-24 text-center">
            <h3 className="text-2xl font-bold text-gray-900 mb-3 tracking-tight">
              {t("noResultsTitle")}
            </h3>
            <p className="text-gray-600 mb-6">{t("noResultsDescription")}</p>
            <button
              onClick={limpiarFiltros}
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#102542] text-white rounded-full text-sm font-semibold hover:bg-[#0a1a30] transition-colors"
            >
              <X className="w-4 h-4" />
              {t("clear")}
            </button>
          </section>
        )}

      {/* Grid */}
      {!loading && concursosFiltrados.length > 0 && (
        <section className="max-w-[80%] mx-auto py-16">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-px bg-gray-200 rounded-2xl overflow-hidden border border-gray-200">
            {paginatedConcursos.map((concurso) => {
              const variant = getEstadoVariant(concurso.estado);
              const estadoClass = ESTADO_STYLES[variant];
              const isOpen = variant === "open";
              const timeRemaining = isOpen
                ? getTimeRemaining(concurso.fecha_fin)
                : null;

              return (
                <article
                  key={concurso.id}
                  className="bg-white p-8 md:p-10 flex flex-col transition-colors hover:bg-gray-50"
                >
                  {/* Estado + Rubros */}
                  <div className="flex flex-wrap items-center gap-2 mb-6">
                    <span
                      className={`inline-flex items-center text-xs uppercase tracking-wider font-semibold border rounded-full px-3 py-1 ${estadoClass}`}
                    >
                      {concurso.estado}
                    </span>
                  </div>

                  {/* Título */}
                  <h2 className="text-lg md:text-xl font-bold text-gray-900 mb-4 tracking-tight leading-snug">
                    {concurso.titulo}
                  </h2>

                  {/* Rubros */}
                  {concurso.rubros.length > 0 && (
                    <div className="flex flex-wrap gap-2 mb-6">
                      {concurso.rubros.map((rubro, index) => (
                        <span
                          key={index}
                          className="inline-flex items-center gap-1.5 text-xs text-gray-600"
                        >
                          <Tag className="w-3 h-3 text-[#102542]" />
                          <span className="font-medium text-gray-900">
                            {rubro.nombre}
                          </span>
                          {rubro.sub_rubro && (
                            <>
                              <ChevronRight className="w-3 h-3 text-gray-400" />
                              <span className="text-gray-500">
                                {rubro.sub_rubro}
                              </span>
                            </>
                          )}
                        </span>
                      ))}
                    </div>
                  )}

                  {/* Metadata */}
                  <dl className="grid grid-cols-1 sm:grid-cols-3 gap-y-4 gap-x-6 py-6 border-y border-gray-100 mb-6">
                    <div>
                      <dt className="text-xs uppercase tracking-wider text-gray-400 font-semibold mb-1">
                        {t("cardCost")}
                      </dt>
                      <dd className="text-sm font-semibold text-[#102542] tabular-nums">
                        {concurso.costo_formatted}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-xs uppercase tracking-wider text-gray-400 font-semibold mb-1">
                        {t("cardStart")}
                      </dt>
                      <dd className="text-sm font-medium text-gray-900 inline-flex items-center gap-1.5">
                        <Calendar className="w-3.5 h-3.5 text-gray-400" />
                        {formatDate(concurso.fecha_inicio, locale)}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-xs uppercase tracking-wider text-gray-400 font-semibold mb-1">
                        {t("cardEnd")}
                      </dt>
                      <dd className="text-sm font-medium text-gray-900 inline-flex items-center gap-1.5">
                        <Calendar className="w-3.5 h-3.5 text-gray-400" />
                        {formatDate(concurso.fecha_fin, locale)}
                      </dd>
                    </div>
                  </dl>

                  {/* Tiempo restante */}
                  {timeRemaining && (
                    <div
                      className={`inline-flex items-center self-start gap-1.5 text-xs uppercase tracking-wider font-semibold mb-6 ${
                        timeRemaining.urgent
                          ? "text-red-600"
                          : "text-emerald-700"
                      }`}
                    >
                      <Clock className="w-3.5 h-3.5" />
                      {timeRemaining.text}
                    </div>
                  )}

                  {/* CTA */}
                  <a
                    href={concurso.documento}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-auto inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full bg-[#102542] text-white text-xs uppercase tracking-wider font-semibold hover:bg-[#0a1a30] transition-colors"
                  >
                    <Download className="w-4 h-4" />
                    {t("downloadDocument")}
                  </a>
                </article>
              );
            })}
          </div>

          {/* Paginación */}
          {totalPages > 1 && (
            <div className="mt-12 flex flex-wrap items-center justify-center gap-2">
              <button
                onClick={() => {
                  setPage((p) => Math.max(1, p - 1));
                  window.scrollTo({ top: 0, behavior: "smooth" });
                }}
                disabled={page === 1}
                className="w-10 h-10 flex items-center justify-center rounded-full border border-gray-200 text-gray-700 disabled:opacity-30 disabled:cursor-not-allowed hover:border-[#102542] hover:text-[#102542] transition-colors"
                aria-label={t("previous")}
              >
                <ChevronLeft className="w-4 h-4" />
              </button>

              {getPageList(page, totalPages).map((pageNum, idx) =>
                pageNum === "..." ? (
                  <span
                    key={`ellipsis-${idx}`}
                    className="w-10 h-10 flex items-center justify-center text-gray-400 font-mono text-sm"
                  >
                    …
                  </span>
                ) : (
                  <button
                    key={pageNum}
                    onClick={() => {
                      setPage(pageNum as number);
                      window.scrollTo({ top: 0, behavior: "smooth" });
                    }}
                    className={`w-10 h-10 rounded-full text-sm font-semibold tabular-nums transition-colors border ${
                      page === pageNum
                        ? "bg-[#102542] text-white border-[#102542]"
                        : "bg-white text-gray-700 border-gray-200 hover:border-[#102542] hover:text-[#102542]"
                    }`}
                  >
                    {pageNum}
                  </button>
                ),
              )}

              <button
                onClick={() => {
                  setPage((p) => Math.min(totalPages, p + 1));
                  window.scrollTo({ top: 0, behavior: "smooth" });
                }}
                disabled={page === totalPages}
                className="w-10 h-10 flex items-center justify-center rounded-full border border-gray-200 text-gray-700 disabled:opacity-30 disabled:cursor-not-allowed hover:border-[#102542] hover:text-[#102542] transition-colors"
                aria-label={t("next")}
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          )}
        </section>
      )}
    </main>
  );
}
